import { cn } from "@/lib/cn";
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";

const LINE =
  "h-12 w-full border-b bg-transparent text-base leading-subtitle text-primary outline-none transition-colors placeholder:text-secondary";

export const Field = forwardRef<
  HTMLInputElement,
  ComponentProps<"input"> & { label: string; error?: string }
>(function Field({ label, error, className, ...props }, ref) {
  return (
    <FieldShell label={label} error={error} className={className}>
      <input
        ref={ref}
        placeholder={label}
        aria-invalid={Boolean(error)}
        className={cn(
          LINE,
          error ? "border-error" : "border-strong focus:border-brand",
        )}
        {...props}
      />
    </FieldShell>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<"textarea"> & { label: string; error?: string }
>(function Textarea({ label, error, className, ...props }, ref) {
  return (
    <FieldShell label={label} error={error} className={className}>
      <textarea
        ref={ref}
        placeholder={label}
        rows={5}
        aria-invalid={Boolean(error)}
        className={cn(
          LINE,
          "h-40 resize-none pt-2",
          error ? "border-error" : "border-strong focus:border-brand",
        )}
        {...props}
      />
    </FieldShell>
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  ComponentProps<"select"> & {
    label: string;
    error?: string;
    options: readonly string[];
  }
>(function Select({ label, error, options, className, ...props }, ref) {
  return (
    <FieldShell label={label} error={error} className={className}>
      <div className="relative">
        <select
          ref={ref}
          defaultValue=""
          aria-invalid={Boolean(error)}
          className={cn(
            LINE,
            "appearance-none pr-8",
            error ? "border-error" : "border-strong focus:border-brand",
          )}
          {...props}
        >
          <option value="" disabled>
            {label}
          </option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <Chevron />
      </div>
    </FieldShell>
  );
});

export function CheckboxGroup({
  legend,
  options,
  registration,
  error,
}: {
  legend: string;
  options: readonly string[];
  registration: UseFormRegisterReturn;
  error?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-6">
      <legend className="flex h-12 items-center text-base leading-subtitle text-secondary">
        {legend}
      </legend>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <label
            key={option}
            className="group flex w-fit cursor-pointer items-center gap-2.5"
          >
            <input
              type="checkbox"
              value={option}
              className="peer sr-only"
              {...registration}
            />
            <span
              aria-hidden
              className="flex size-6 shrink-0 items-center justify-center rounded border border-primary bg-primary transition-colors peer-checked:border-brand peer-checked:bg-brand peer-checked:[&>svg]:opacity-100 peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                className="size-3.5 text-inverse opacity-0 transition-opacity"
              >
                <path
                  d="M3 8.5 6.5 12 13 4.5"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="text-base leading-loose text-secondary">
              {option}
            </span>
          </label>
        ))}
      </div>
      {error && <p className="text-sm text-error">{error}</p>}
    </fieldset>
  );
}

function FieldShell({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("flex min-w-0 flex-1 flex-col gap-1", className)}>
      <span className="sr-only">{label}</span>
      {children}
      {error && <span className="text-sm text-error">{error}</span>}
    </label>
  );
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="pointer-events-none absolute right-0 top-1/2 size-4 -translate-y-1/2 text-secondary"
    >
      <path
        d="m4 6 4 4 4-4"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
